"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import Link from "@/components/ui/intent-link";
import { ArrowRight, Check, Download, Search } from "lucide-react";

import { AssetImage } from "@/components/optilase/asset-image";
import { GoogleMark, RelativeTime, StarRating, formatName } from "@/components/reviews/review-card";
import type { GoogleReview } from "@/lib/google-reviews";
import { RegionSwitcher } from "@/components/optilase/region-switcher";
import { useTriggerAction } from "@/lib/action-link";
import { BRAND } from "@/lib/brand";
import { regionPath } from "@/lib/region";
import type { Region } from "@/lib/region";
import { useMenuDrawer } from "@/lib/menu-context";
import { useSearch } from "@/lib/search-context";
import { useSolidNav } from "@/lib/solid-nav";
import { titleCase } from "@/lib/title-case";
import { cn } from "@/lib/utils";
import { bookingLabel, offersFreeConsultation } from "@/lib/consultation";
import { usePublicPathname } from "@/lib/public-pathname";

export type NavLink = {
  label: string;
  href: string;
};
export type NavColumn = {
  heading?: string;
  headingHref?: string;
  nested?: boolean;
  panel?: boolean;
  links: NavLink[];
};

export type NavFeature = { kind: "review" | "booking" | "post" | "quiz" };

export type NavItem = {
  label: string;
  href: string;
  columns?: NavColumn[];
  viewAll?: { label: string; href: string };
  feature?: NavFeature;
  download?: { label: string; note?: string; href: string };
};

export type NavFeatureData = {
  review?: GoogleReview | null;
  post?: {
    title: string;
    href: string;
    image?: string;
    focus?: string;
    alt?: string;
    meta?: string;
    category?: string;
  } | null;
  images?: {
    conditions?: { src: string; focus?: string; alt?: string } | null;
    pricing?: { src: string; focus?: string; alt?: string } | null;
  };
};

export type HeaderViewProps = {
  items: NavItem[];
  featureData?: NavFeatureData;
  ctaLabel?: string;
  region?: Region;
  variant?: "overlay" | "solid";
};

function isCurrent(href: string, pathname: string | null): boolean {
  if (!pathname || href === "#") return false;
  return href.replace(/\/+$/, "") === pathname.replace(/\/+$/, "");
}

function isActiveSection(href: string, pathname: string | null): boolean {
  if (!pathname || href === "#") return false;
  const a = href.replace(/\/+$/, "");
  const b = pathname.replace(/\/+$/, "");
  return a !== "" && (b === a || b.startsWith(`${a}/`));
}

function NavPanelFeature({
  feature,
  data,
  tabbable,
  onAction,
}: {
  feature: NavFeature;
  data?: NavFeatureData;
  tabbable: boolean;
  onAction: () => void;
}) {
  const tab = tabbable ? undefined : -1;
  const free = offersFreeConsultation(usePublicPathname());

  if (feature.kind === "review") {
    const review = data?.review;
    return (
      <>
        <NavPanelImage image={data?.images?.conditions} className="w-[277px]" />
        {review ? (
          <div className="relative hidden w-[277px] shrink-0 lg:block">
            <NavReviewTile review={review} />
          </div>
        ) : null}
      </>
    );
  }

  if (feature.kind === "booking") {
    return (
      <>
        <NavPanelImage image={data?.images?.pricing} className="w-[293px]" />
        <div className="bg-teal-01 hidden w-[293px] shrink-0 flex-col gap-4 rounded-2xl p-6 lg:flex">
          <p className="text-grey-08 text-[22px] leading-[29px] font-bold">
            Find the right vision solution for you.
            <span className="text-teal-08 block">Book a consultation.</span>
          </p>
          <ul className="flex flex-col gap-2">
            {BOOKING_POINTS.map((point) => (
              <li key={point} className="text-grey-08 flex items-center gap-2 text-sm leading-4">
                <Check className="text-teal-08 size-[13px] shrink-0" strokeWidth={3} aria-hidden />
                {point}
              </li>
            ))}
          </ul>
          <PanelActionButton
            action="open_booking"
            label={free ? "Book a free consultation" : "Book consultation"}
            tab={tab}
            block
            onAction={onAction}
          />
        </div>
      </>
    );
  }

  if (feature.kind === "quiz") {
    return (
      <div className="bg-teal-01 hidden w-[277px] shrink-0 flex-col gap-3 rounded-2xl p-6 lg:flex">
        <span className="text-teal-08 text-sm font-bold tracking-[0.08em] uppercase">
          Not sure yet?
        </span>
        <p className="text-grey-08 text-lg leading-snug font-bold">
          Which treatment could be right for you?
        </p>
        <p className="text-grey-07 text-sm leading-relaxed">
          Seven quick questions about your eyes. We suggest the treatment most likely to suit you.
        </p>
        <PanelActionButton action="open_quiz" label="Take the quiz" tab={tab} onAction={onAction} />
      </div>
    );
  }

  const post = data?.post;
  if (!post) return null;
  return (
    <Link
      href={post.href}
      tabIndex={tab}
      onClick={onAction}
      className="bg-navy-03 relative hidden w-[277px] shrink-0 overflow-hidden rounded-2xl lg:block"
    >
      {post.image ? (
        <AssetImage
          src={post.image}
          alt={post.alt ?? ""}
          focus={post.focus}
          width={560}
          height={640}
          className="absolute inset-0 size-full object-cover"
        />
      ) : null}
      <span className="from-navy-03 via-navy-03/55 absolute inset-x-0 bottom-0 h-3/5 bg-linear-to-t to-transparent" />
      <span className="relative flex h-full min-h-[323px] flex-col justify-end gap-1 p-6">
        {post.category ? (
          <span className="text-teal-04 text-xs font-bold tracking-[0.08em] uppercase">
            {post.category}
          </span>
        ) : null}
        <span className="text-[19px] leading-6 font-bold text-white">{post.title}</span>
        {post.meta ? <span className="text-sm text-white/80">{post.meta}</span> : null}
      </span>
    </Link>
  );
}

function NavQuizBanner({ tabbable, onAction }: { tabbable: boolean; onAction: () => void }) {
  return (
    <div className="bg-teal-01 flex items-center gap-6 rounded-2xl px-6 py-5">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="text-grey-08 text-lg leading-snug font-bold">
          Not sure which treatment is right for you?
        </p>
        <p className="text-grey-07 text-sm leading-relaxed">
          Seven quick questions about your eyes, and we suggest the one most likely to suit you.
        </p>
      </div>
      <PanelActionButton
        action="open_quiz"
        label="Take the quiz"
        tab={tabbable ? undefined : -1}
        onAction={onAction}
      />
    </div>
  );
}

function NavPanelImage({
  image,
  className,
}: {
  image?: { src: string; focus?: string; alt?: string } | null;
  className: string;
}) {
  if (!image) return null;
  return (
    <div className={cn("hidden shrink-0 overflow-hidden rounded-2xl lg:block", className)}>
      <AssetImage
        src={image.src}
        focus={image.focus}
        alt={image.alt ?? ""}
        width={600}
        height={700}
        className="size-full object-cover"
      />
    </div>
  );
}

function NavReviewTile({ review }: { review: GoogleReview }) {
  return (
    <aside className="bg-teal-01 absolute inset-0 flex flex-col overflow-hidden rounded-2xl p-6">
      <div className="mb-3 flex items-center justify-between gap-3">
        <StarRating rating={review.rating} />
        <GoogleMark label={false} />
      </div>

      <div className="relative mb-4 min-h-0 flex-1">
        <div
          tabIndex={0}
          role="group"
          aria-label="Patient review"
          className="focus-visible:ring-teal-07/40 h-full scrollbar-none overflow-y-auto pr-1 pb-8 outline-none focus-visible:ring-2"
        >
          <p className="text-grey-08 text-sm leading-relaxed">{review.text}</p>
        </div>
        <span
          aria-hidden
          className="from-teal-01 pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-linear-to-t to-transparent"
        />
      </div>

      <div className="flex items-center gap-3">
        <span className="bg-brand-orange-02 flex size-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white">
          {review.authorName.charAt(0).toUpperCase()}
        </span>
        <span className="flex flex-col leading-tight">
          <span className="text-grey-09 text-sm font-bold">{formatName(review.authorName)}</span>
          <span className="text-grey-06 text-xs">
            <RelativeTime timestamp={review.time} />
          </span>
        </span>
      </div>
    </aside>
  );
}

const NAV_LINK_CLASS =
  "block w-fit rounded-lg px-3 py-[7px] text-left text-base transition-colors hover:text-teal-08";

function PanelActionButton({
  action,
  label,
  tab,
  block,
  onAction,
}: {
  action: string;
  label: string;
  tab: number | undefined;
  block?: boolean;
  onAction: () => void;
}) {
  const trigger = useTriggerAction();
  return (
    <button
      type="button"
      tabIndex={tab}
      onClick={(e) => {
        onAction();
        trigger(action, e);
      }}
      className={cn(
        "bg-teal-08 hover:bg-teal-07 flex h-12 shrink-0 cursor-pointer items-center justify-center rounded-xl px-5 text-base font-bold text-white transition-colors",
        block && "mt-auto",
      )}
    >
      {label}
    </button>
  );
}

const BOOKING_POINTS = [
  "Laser Lifetime Care Guarantee",
  "Clinical Experts",
  "World-Renowned Surgeons",
];

export default function HeaderView({
  featureData,
  items,
  ctaLabel = "Book consultation",
  region = "ie",
  variant,
}: HeaderViewProps) {
  const trigger = useTriggerAction();
  const { setOpen: setSearchOpen } = useSearch();
  const { setOpen: setMenuOpen } = useMenuDrawer();
  const pathname = usePublicPathname();
  const headerCtaLabel = bookingLabel(ctaLabel, offersFreeConsultation(pathname));
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [suppressed, setSuppressed] = useState(false);
  const topRefs = useRef<(HTMLElement | null)[]>([]);
  const [navRove, setNavRove] = useState(0);
  const close = useCallback(() => {
    setOpenIndex(null);
    setSuppressed(true);
  }, []);
  const handleTopKey = (
    e: React.KeyboardEvent<HTMLElement>,
    index: number,
    isMega: boolean,
    total: number,
  ) => {
    const isLeft = e.key === "ArrowLeft";
    const isRight = e.key === "ArrowRight";
    if (isLeft || isRight) {
      e.preventDefault();
      e.stopPropagation();
      setOpenIndex(null);
      const next = isRight ? Math.min(index + 1, total - 1) : Math.max(index - 1, 0);
      topRefs.current[next]?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      setOpenIndex(null);
      topRefs.current[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      setOpenIndex(null);
      topRefs.current[total - 1]?.focus();
    } else if (isMega && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      const wrapper = e.currentTarget.parentElement;
      flushSync(() => setOpenIndex(index));
      const target = wrapper?.querySelector<HTMLElement>(
        '.nav-mega a[href], .nav-mega button:not([aria-haspopup="true"])',
      );
      if (target) {
        void target.offsetHeight;
        target.focus();
      }
    } else if (e.key === "Escape") {
      setOpenIndex(null);
    }
  };

  const handleMegaInsideKey = (
    e: React.KeyboardEvent<HTMLElement>,
    topIndex: number,
    total: number,
  ) => {
    const target = e.target as HTMLElement;
    if (target.matches('button[aria-haspopup="true"]')) return;

    if (e.key === "Escape") {
      e.preventDefault();
      setOpenIndex(null);
      topRefs.current[topIndex]?.focus();
      return;
    }
    if (e.key === "Tab") {
      e.preventDefault();
      setOpenIndex(null);
      const next = e.shiftKey ? (topIndex - 1 + total) % total : (topIndex + 1) % total;
      topRefs.current[next]?.focus();
      return;
    }
    const items = Array.from(
      e.currentTarget.querySelectorAll<HTMLElement>(
        '.nav-mega a[href], .nav-mega button:not([aria-haspopup="true"])',
      ),
    );
    if (items.length === 0) return;
    if (e.key === "Home") {
      e.preventDefault();
      items[0]?.focus();
      return;
    }
    if (e.key === "End") {
      e.preventDefault();
      items[items.length - 1]?.focus();
      return;
    }
    if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
    e.preventDefault();
    const idx = items.indexOf(target);
    const next =
      e.key === "ArrowDown"
        ? idx < 0
          ? 0
          : (idx + 1) % items.length
        : idx < 0
          ? items.length - 1
          : (idx - 1 + items.length) % items.length;
    items[next]?.focus();
  };

  const navRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (openIndex === null) return;
    const onDown = (e: PointerEvent) => {
      if (!navRef.current?.contains(e.target as Node)) setOpenIndex(null);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [openIndex]);

  const [isCoarse, setIsCoarse] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(pointer: coarse)");
    const sync = () => setIsCoarse(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  const home = (pathname || "/").replace(/\/+$/, "");
  const isHome = home === "" || home === "/uk";
  const pinnedSolid = useSolidNav();
  const overlay = (variant ?? (isHome && !pinnedSolid ? "overlay" : "solid")) === "overlay";
  const isItemActive = (item: NavItem) =>
    isActiveSection(item.href, pathname) ||
    (item.columns ?? []).some((c) => c.links.some((l) => isActiveSection(l.href, pathname)));

  return (
    <header
      className={cn(
        "group/nav z-30 w-full",
        overlay && "absolute inset-x-0 top-[var(--region-banner-h,0px)]",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "pointer-events-none fixed inset-0 -z-10 hidden bg-black/60 backdrop-blur-[2px] transition-opacity duration-200 lg:block",
          openIndex !== null ? "opacity-100" : "opacity-0",
          !isCoarse && !suppressed && "group-has-[.nav-mega:hover]/nav:opacity-100",
        )}
      />

      <div
        data-site-header
        className={cn(
          "relative w-full",
          overlay ? "bg-linear-to-b from-black/50 via-black/25 via-60% to-transparent" : "bg-white",
        )}
      >
        <div className="relative mx-auto flex h-20 max-w-360 items-center justify-between px-5 py-5 lg:h-26 lg:px-20 lg:py-8">
          <Link
            href={regionPath("/", region)}
            aria-label={`${BRAND.name} — home`}
            className="shrink-0"
          >
            <AssetImage
              data-site-logo="default"
              src={
                overlay ? "/assets/logo/optilase/logo-white.svg" : "/assets/logo/optilase/logo.svg"
              }
              alt={BRAND.logoAlt}
              width={125}
              height={45}
              className="h-9 w-auto lg:h-11.25"
              priority
            />
            <AssetImage
              data-site-logo="on-dark"
              src="/assets/logo/optilase/logo-white-strapline.svg"
              alt={BRAND.logoAlt}
              aria-hidden
              width={125}
              height={45}
              className="hidden h-9 w-auto lg:h-11.25"
            />
          </Link>

          <nav
            ref={navRef}
            aria-label="Main"
            className="hidden lg:block"
            onPointerLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) setSuppressed(false);
            }}
          >
            <ul className="flex items-center gap-8">
              {items.map((item, index) => {
                const active = isItemActive(item);
                const wide = item.feature?.kind === "quiz";
                const pill = (
                  <span
                    aria-hidden
                    className={cn(
                      "pointer-events-none absolute -inset-x-3 -inset-y-2 -z-10 rounded-xl transition-opacity duration-200 group-focus-within/item:opacity-100 group-hover/item:opacity-100",
                      overlay ? "bg-white" : "bg-teal-01",
                      active || openIndex === index ? "opacity-100" : "opacity-0",
                    )}
                  />
                );
                const labelClass = cn(
                  "relative isolate inline-flex items-center gap-1.5 rounded-xl text-base font-medium whitespace-nowrap transition-colors",
                  overlay
                    ? openIndex === index || active
                      ? "text-navy-03"
                      : "text-white group-hover/item:text-navy-03 group-focus-within/item:text-navy-03"
                    : "text-navy-03",
                );

                if (!item.columns?.length) {
                  return (
                    <li key={item.label} className="group/item relative">
                      <Link
                        href={item.href}
                        ref={(el) => {
                          topRefs.current[index] = el;
                        }}
                        aria-current={active ? "page" : undefined}
                        tabIndex={index === navRove ? 0 : -1}
                        onFocus={() => setNavRove(index)}
                        onKeyDown={(e) => handleTopKey(e, index, false, items.length)}
                        className={labelClass}
                      >
                        {pill}
                        {titleCase(item.label)}
                      </Link>
                    </li>
                  );
                }

                return (
                  <li
                    key={item.label}
                    className="nav-mega group/item static"
                    onBlur={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                        setOpenIndex(null);
                      }
                    }}
                    onKeyDown={(e) => handleMegaInsideKey(e, index, items.length)}
                  >
                    <button
                      type="button"
                      ref={(el) => {
                        topRefs.current[index] = el;
                      }}
                      aria-haspopup="true"
                      aria-expanded={openIndex === index}
                      aria-current={active ? "page" : undefined}
                      tabIndex={index === navRove ? 0 : -1}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={
                        isCoarse
                          ? () => setOpenIndex((cur) => (cur === index ? null : index))
                          : undefined
                      }
                      onFocus={() => setNavRove(index)}
                      onKeyDown={(e) => handleTopKey(e, index, true, items.length)}
                      className={cn(labelClass, "cursor-default")}
                    >
                      {pill}
                      {titleCase(item.label)}
                    </button>

                    <div
                      onClick={close}
                      className={cn(
                        "absolute top-full left-1/2 w-full max-w-[1000px] -translate-x-1/2 px-5 pt-2 transition-all duration-200",
                        !suppressed && "group-hover/item:visible group-hover/item:opacity-100",
                        openIndex === index ? "visible opacity-100" : "invisible opacity-0",
                      )}
                    >
                      <span aria-hidden className="absolute inset-x-0 -top-8 h-10" />
                      <div
                        className={cn(
                          "relative z-10 flex min-h-[434px] origin-top flex-col rounded-3xl bg-white p-10 shadow-[0_30px_60px_-20px_rgba(5,29,45,0.25)] transition-all duration-200",
                          !suppressed &&
                            "group-hover/item:translate-y-0 group-hover/item:scale-100",
                          openIndex === index
                            ? "translate-y-0 scale-100"
                            : "-translate-y-1 scale-[0.99]",
                        )}
                      >
                        <div className={cn("flex gap-6", wide ? "" : "flex-1 items-stretch")}>
                          <div className="flex min-w-0 flex-1 flex-col">
                            <div
                              className={cn(
                                "grid flex-1 gap-x-6 gap-y-6",
                                wide && "content-start",
                                item.viewAll && "pb-6",
                              )}
                              style={{
                                gridTemplateColumns: `repeat(${
                                  item.feature && !wide
                                    ? Math.min(item.columns.length, 2)
                                    : item.columns.length
                                }, minmax(0, 1fr))`,
                              }}
                            >
                              {item.columns.map((col, ci) => (
                                <div
                                  key={col.heading ?? ci}
                                  className={cn(
                                    "flex flex-col gap-2 pt-4",
                                    col.panel ? "bg-teal-01 rounded-2xl px-3 pb-4" : "-mx-3",
                                  )}
                                >
                                  {col.heading ? (
                                    <div className="text-teal-08 w-fit text-base font-bold">
                                      {col.headingHref ? (
                                        <Link
                                          href={col.headingHref}
                                          aria-current={
                                            isCurrent(col.headingHref, pathname)
                                              ? "page"
                                              : undefined
                                          }
                                          tabIndex={openIndex === index ? undefined : -1}
                                          className="hover:text-teal-09 block rounded-lg px-3 py-[7px] underline-offset-4 hover:underline"
                                        >
                                          {titleCase(col.heading)}
                                        </Link>
                                      ) : (
                                        <span className="block px-3 py-[7px]">
                                          {titleCase(col.heading)}
                                        </span>
                                      )}
                                    </div>
                                  ) : null}
                                  <ul className="flex flex-col gap-2">
                                    {col.links.map((l) => (
                                      <li key={l.href}>
                                        <Link
                                          href={l.href}
                                          aria-current={
                                            isCurrent(l.href, pathname) ? "page" : undefined
                                          }
                                          tabIndex={openIndex === index ? undefined : -1}
                                          className={cn(
                                            NAV_LINK_CLASS,
                                            "text-grey-08",
                                            isCurrent(l.href, pathname) &&
                                              (col.panel ? "bg-teal-03" : "bg-teal-02"),
                                          )}
                                        >
                                          {titleCase(l.label)}
                                        </Link>
                                      </li>
                                    ))}
                                  </ul>

                                  {item.viewAll && wide && ci === item.columns!.length - 1 ? (
                                    <Link
                                      href={item.viewAll.href}
                                      tabIndex={openIndex === index ? undefined : -1}
                                      className="text-teal-07 hover:text-teal-08 mt-auto inline-flex w-fit items-center gap-2 px-3 py-[7px] text-base font-bold transition-colors"
                                    >
                                      {titleCase(item.viewAll.label)}
                                      <ArrowRight className="size-5" strokeWidth={2.25} />
                                    </Link>
                                  ) : null}
                                </div>
                              ))}
                            </div>

                            {item.viewAll && !wide ? (
                              <div className="border-grey-01 mt-auto border-t pt-5">
                                <Link
                                  href={item.viewAll.href}
                                  tabIndex={openIndex === index ? undefined : -1}
                                  className="text-teal-07 hover:text-teal-08 inline-flex items-center gap-2 text-base font-bold transition-colors"
                                >
                                  {titleCase(item.viewAll.label)}
                                  <ArrowRight className="size-5" strokeWidth={2.25} />
                                </Link>
                              </div>
                            ) : null}

                            {item.download ? (
                              <div className="border-grey-01 mt-auto border-t pt-5">
                                <p className="text-grey-08 text-base font-bold">
                                  {item.download.label}
                                </p>
                                {item.download.note ? (
                                  <p className="text-grey-06 mt-1 text-sm leading-snug">
                                    {item.download.note}
                                  </p>
                                ) : null}
                                <a
                                  href={item.download.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  tabIndex={openIndex === index ? undefined : -1}
                                  className="border-teal-03 text-teal-07 hover:bg-teal-07 mt-4 inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-base font-bold transition-colors hover:text-white"
                                >
                                  <Download className="size-4" strokeWidth={2.25} aria-hidden />
                                  Download price guide
                                </a>
                              </div>
                            ) : null}
                          </div>

                          {item.feature && !wide ? (
                            <NavPanelFeature
                              feature={item.feature}
                              data={featureData}
                              tabbable={openIndex === index}
                              onAction={close}
                            />
                          ) : null}
                        </div>

                        {wide ? (
                          <div className="border-grey-01 mt-auto border-t pt-6">
                            <NavQuizBanner tabbable={openIndex === index} onAction={close} />
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="flex shrink-0 items-center gap-5 lg:hidden">
            <button
              type="button"
              onClick={(e) => trigger("open_booking", e)}
              className={cn(
                "hidden h-10 cursor-pointer items-center rounded-full px-5 text-sm font-semibold transition-colors md:inline-flex",
                overlay
                  ? "text-navy-03 hover:bg-teal-03 bg-white"
                  : "bg-teal-07 hover:bg-teal-08 text-white",
              )}
            >
              {headerCtaLabel}
            </button>
            <button
              type="button"
              onClick={() => setSearchOpen(true, "header")}
              aria-label="Search"
              className={cn(
                "cursor-pointer transition-opacity hover:opacity-80",
                overlay ? "text-white" : "text-teal-07",
              )}
            >
              <Search className="size-6" strokeWidth={2} />
            </button>
            <button
              type="button"
              data-menu-toggle
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              className={cn(
                "cursor-pointer transition-opacity hover:opacity-80",
                overlay ? "text-white" : "text-teal-07",
              )}
            >
              <svg
                width="26"
                height="16"
                viewBox="0 0 26 16"
                fill="none"
                aria-hidden
                className="w-[26px] lg:w-[27px]"
              >
                <path
                  d="M1 1h24M1 8h24M1 15h24"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          <div className="hidden shrink-0 items-center gap-3 lg:flex">
            <button
              type="button"
              onClick={() => setSearchOpen(true, "header")}
              aria-label="Search"
              className={cn(
                "inline-flex cursor-pointer items-center justify-center rounded-full p-1 transition-opacity hover:opacity-80",
                overlay ? "text-white" : "text-navy-03",
              )}
            >
              <Search className="size-4.5" strokeWidth={2} />
            </button>

            <span
              aria-hidden
              className={cn("h-4 w-px", overlay ? "bg-white/25" : "bg-navy-03/15")}
            />

            <RegionSwitcher region={region} variant={variant} />

            <button
              type="button"
              onClick={(e) => trigger("open_booking", e)}
              className={cn(
                "ml-1 flex h-10 cursor-pointer items-center rounded-full px-5 text-sm font-semibold transition-colors",
                overlay
                  ? "text-navy-03 hover:bg-teal-03 bg-white"
                  : "bg-teal-07 hover:bg-teal-08 text-white",
              )}
            >
              {headerCtaLabel}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
